import {
  hasStatus,
  isEmptyEntry,
  remoteWins,
  withStatus,
  type LibraryEntry,
  type LibraryStatus,
  type Synced,
  type WatchProgress,
} from "./types";

interface Snapshot {
  library: Record<string, Synced<LibraryEntry>>;
  progress: Record<string, Synced<WatchProgress>>;
  cursors: { library: string | null; progress: string | null };
}

const STORAGE_KEY = "animflix.library.v1";
const EMPTY: Snapshot = { library: {}, progress: {}, cursors: { library: null, progress: null } };

/** Acceso tolerante a fallos: en modo privado o con almacenamiento bloqueado se trabaja en memoria. */
function load(storage: Storage | null): Snapshot {
  try {
    const raw = storage?.getItem(STORAGE_KEY);
    return raw ? { ...EMPTY, ...(JSON.parse(raw) as Snapshot) } : structuredClone(EMPTY);
  } catch {
    return structuredClone(EMPTY);
  }
}

/**
 * Biblioteca local (fuente de verdad en el navegador), equivalente a Room en Android.
 * Cada cambio queda `pending` hasta que el sincronizador lo confirma con la nube.
 * Se integra con React mediante subscribe/getSnapshot (useSyncExternalStore).
 */
export class LocalLibraryStore {
  private state: Snapshot;
  private listeners = new Set<() => void>();

  constructor(
    private readonly storage: Storage | null,
    private readonly now: () => number = Date.now
  ) {
    this.state = load(storage);
  }

  // --- React ---
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): Snapshot => this.state;

  private commit(next: Snapshot) {
    this.state = next;
    try {
      this.storage?.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      // Sin cuota o almacenamiento bloqueado: el estado sigue vivo en memoria.
    }
    this.listeners.forEach((listener) => listener());
  }

  // --- Biblioteca ---
  entry(animeUrl: string): LibraryEntry | undefined {
    const row = this.state.library[animeUrl];
    return row && row.deletedAt === null ? row : undefined;
  }

  entriesWith(status: LibraryStatus): LibraryEntry[] {
    return Object.values(this.state.library)
      .filter((row) => row.deletedAt === null && hasStatus(row, status))
      .sort((a, b) => b.updatedAt - a.updatedAt);
  }

  setStatus(anime: Pick<LibraryEntry, "animeUrl" | "title" | "imageUrl" | "provider">, status: LibraryStatus, enabled: boolean) {
    const now = this.now();
    const stored = this.entry(anime.animeUrl);
    const base: LibraryEntry = stored
      ? { ...stored, title: anime.title || stored.title, imageUrl: anime.imageUrl || stored.imageUrl, provider: anime.provider || stored.provider }
      : { ...anime, isFavorite: false, isWatching: false, isFinished: false, isWatchLater: false, updatedAt: now, deletedAt: null };
    const updated = withStatus(base, status, enabled);
    const row: Synced<LibraryEntry> = { ...updated, updatedAt: now, deletedAt: isEmptyEntry(updated) ? now : null, pending: true };
    this.commit({ ...this.state, library: { ...this.state.library, [anime.animeUrl]: row } });
  }

  // --- Progreso ---
  progress(episodeUrl: string): WatchProgress | undefined {
    const row = this.state.progress[episodeUrl];
    return row && row.deletedAt === null ? row : undefined;
  }

  recentProgress(limit = 20): WatchProgress[] {
    return Object.values(this.state.progress)
      .filter((row) => row.deletedAt === null)
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .slice(0, limit);
  }

  saveProgress(progress: Omit<WatchProgress, "updatedAt" | "deletedAt">) {
    const row: Synced<WatchProgress> = { ...progress, updatedAt: this.now(), deletedAt: null, pending: true };
    this.commit({ ...this.state, progress: { ...this.state.progress, [progress.episodeUrl]: row } });
  }

  deleteProgress(episodeUrl: string) {
    const current = this.state.progress[episodeUrl];
    if (!current || current.deletedAt !== null) return;
    const now = this.now();
    this.commit({
      ...this.state,
      progress: { ...this.state.progress, [episodeUrl]: { ...current, updatedAt: now, deletedAt: now, pending: true } },
    });
  }

  /** Marca o desmarca un episodio como visto completo directamente desde la ficha del anime (paridad con Android). */
  toggleEpisodeWatched(
    episode: Pick<WatchProgress, "episodeUrl" | "animeUrl" | "animeTitle" | "episodeTitle" | "imageUrl">,
    watched: boolean
  ) {
    if (watched) {
      const defaultDurationMs = 24 * 60_000;
      this.saveProgress({
        ...episode,
        positionMs: defaultDurationMs,
        durationMs: defaultDurationMs,
      });
    } else {
      this.deleteProgress(episode.episodeUrl);
    }
  }

  // --- Respaldo local (Exportar / Importar JSON) ---
  exportBackup(): string {
    return JSON.stringify(
      {
        version: 1,
        exportedAt: this.now(),
        library: this.state.library,
        progress: this.state.progress,
      },
      null,
      2
    );
  }

  importBackup(rawJson: string): { libraryCount: number; progressCount: number } {
    const parsed = JSON.parse(rawJson) as {
      library?: Record<string, Synced<LibraryEntry>>;
      progress?: Record<string, Synced<WatchProgress>>;
    };
    if (!parsed || typeof parsed !== "object") {
      throw new Error("Archivo de respaldo inválido");
    }
    const nextLibrary = { ...this.state.library };
    const nextProgress = { ...this.state.progress };
    let libraryCount = 0;
    let progressCount = 0;

    if (parsed.library && typeof parsed.library === "object") {
      for (const [key, entry] of Object.entries(parsed.library)) {
        if (entry && typeof entry.animeUrl === "string" && typeof entry.updatedAt === "number") {
          if (remoteWins(nextLibrary[key], entry)) {
            nextLibrary[key] = { ...entry, pending: true };
            libraryCount += 1;
          }
        }
      }
    }
    if (parsed.progress && typeof parsed.progress === "object") {
      for (const [key, prog] of Object.entries(parsed.progress)) {
        if (prog && typeof prog.episodeUrl === "string" && typeof prog.updatedAt === "number") {
          if (remoteWins(nextProgress[key], prog)) {
            nextProgress[key] = { ...prog, pending: true };
            progressCount += 1;
          }
        }
      }
    }
    this.commit({ ...this.state, library: nextLibrary, progress: nextProgress });
    return { libraryCount, progressCount };
  }

  // --- Sincronizacion ---
  pending() {
    return {
      library: Object.values(this.state.library).filter((row) => row.pending),
      progress: Object.values(this.state.progress).filter((row) => row.pending),
    };
  }

  /** Marca como confirmadas las versiones enviadas; si cambiaron mientras tanto siguen pendientes. */
  markSynced(library: LibraryEntry[], progress: WatchProgress[]) {
    const nextLibrary = { ...this.state.library };
    for (const sent of library) {
      const row = nextLibrary[sent.animeUrl];
      if (row && row.updatedAt === sent.updatedAt) nextLibrary[sent.animeUrl] = { ...row, pending: false };
    }
    const nextProgress = { ...this.state.progress };
    for (const sent of progress) {
      const row = nextProgress[sent.episodeUrl];
      if (row && row.updatedAt === sent.updatedAt) nextProgress[sent.episodeUrl] = { ...row, pending: false };
    }
    this.commit({ ...this.state, library: nextLibrary, progress: nextProgress });
  }

  /** Aplica cambios remotos con last-write-wins y guarda el cursor. Devuelve cuantos aplico. */
  applyRemote(library: LibraryEntry[], progress: WatchProgress[], cursors: Partial<Snapshot["cursors"]>): number {
    let applied = 0;
    const nextLibrary = { ...this.state.library };
    for (const remote of library) {
      if (remoteWins(nextLibrary[remote.animeUrl], remote)) {
        nextLibrary[remote.animeUrl] = { ...remote, pending: false };
        applied++;
      }
    }
    const nextProgress = { ...this.state.progress };
    for (const remote of progress) {
      if (remoteWins(nextProgress[remote.episodeUrl], remote)) {
        nextProgress[remote.episodeUrl] = { ...remote, pending: false };
        applied++;
      }
    }
    this.commit({ library: nextLibrary, progress: nextProgress, cursors: { ...this.state.cursors, ...cursors } });
    return applied;
  }

  /** Borra los tombstones ya confirmados por el servidor. */
  purgeSyncedTombstones() {
    const keep = <T extends { deletedAt: number | null; pending: boolean }>(rows: Record<string, T>) =>
      Object.fromEntries(Object.entries(rows).filter(([, row]) => row.deletedAt === null || row.pending));
    this.commit({ ...this.state, library: keep(this.state.library), progress: keep(this.state.progress) });
  }

  cursors() {
    return this.state.cursors;
  }
}
